import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, runSupabaseWakeUp, refreshSessionAndWakeUp } from '../lib/supabase';
import {
  IconPlus,
  IconSearch,
  IconGrid,
  IconList,
  IconEdit,
} from './Icons';
import { useAuth } from '../contexts/AuthContext';
import { normalizeRole, ROLE_OPTIONS, resolveAllowedTabPaths, applyTabRoleForCustomTabs } from '../lib/roles';
import ProfileTabAccessFields from './ProfileTabAccessFields';
import { sendHrUserInviteEmail } from '../services/emailService';
import './TeamMembers.css';

/** Cold starts / slow networks can exceed 12s; retry once after session refresh. */
const FETCH_TIMEOUT_MS = 45000;

const ROLES = { ADMIN: 'admin', HR: 'hr' };

const emptyEditForm = () => ({
  full_name: '',
  role: 'team member',
  Designation: '',
  annual_leave_left: '',
  emergency_leave_left: '',
  sick_leave_left: '',
  avatar_url: '',
  active: true,
  tabAccessCustom: false,
  tabPaths: [],
});

export default function TeamMembers() {
  const { profile, user, refreshProfile } = useAuth();
  const role = normalizeRole(profile?.role);
  const canEdit = role === ROLES.ADMIN || role === ROLES.HR;

  const [viewMode, setViewMode] = useState('table'); // 'table' | 'grid'
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'inactive'
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    email: '',
    fullName: '',
    role: 'team member',
    tabAccessCustom: false,
    tabPaths: [],
  });
  const [addUserError, setAddUserError] = useState(null);
  const [addUserSuccess, setAddUserSuccess] = useState(null);
  const [addingUser, setAddingUser] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef(null);
  const [toggleError, setToggleError] = useState(null);

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const location = useLocation();
  const mountedRef = useRef(true);

  const runWakeUp = useCallback(() => runSupabaseWakeUp(), []);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    const loadOnce = () => {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      return Promise.race([
        supabase.from('profiles').select('*').order('full_name', { ascending: true, nullsFirst: false }),
        timeoutPromise,
      ]);
    };

    try {
      let res;
      try {
        res = await loadOnce();
      } catch (e) {
        if (e?.message?.includes('timed out')) {
          await refreshSessionAndWakeUp();
          res = await loadOnce();
        } else {
          throw e;
        }
      }
      const { data, error } = res;
      if (error) {
        setLoadError(error.message);
        setMembers([]);
      } else {
        setMembers(data ?? []);
      }
    } catch (e) {
      setLoadError(
        e?.message?.includes('timed out')
          ? `${e.message} Check your network and Supabase project; if the tab was in the background, try again.`
          : e?.message || 'Failed to load',
      );
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshSessionAndFetch = useCallback(async () => {
    mountedRef.current = true;
    setLoadError(null);
    try {
      await refreshSessionAndWakeUp();
      await fetchMembers();
    } catch (e) {
      setLoadError(e?.message || 'Failed to load');
    }
  }, [fetchMembers]);

  useEffect(() => {
    if (location.pathname !== '/team/members') return;
    let cancelled = false;
    mountedRef.current = true;
    (async () => {
      const { error: sessionErr } = await supabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setLoadError(sessionErr.message);
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await fetchMembers();
    })();
    return () => { cancelled = true; mountedRef.current = false; };
  }, [location.pathname, runWakeUp, fetchMembers]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const getDesignation = (m) => m?.Designation ?? m?.designation ?? '';

  const generatePasswordFromEmail = (email) => {
    const s = (email || '').trim().toLowerCase();
    const firstTwo = s.slice(0, 2);
    return (firstTwo || 'ab') + '123@';
  };

  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    const { email, fullName } = addUserForm;
    const emailTrim = email?.trim();
    if (!emailTrim) {
      setAddUserError('Email is required.');
      return;
    }
    const roleForApi = addUserForm.tabAccessCustom
      ? 'admin'
      : (addUserForm.role || 'team member').trim().toLowerCase();
    const password = generatePasswordFromEmail(emailTrim);
    setAddUserError(null);
    setAddUserSuccess(null);
    setAddingUser(true);
    try {
      await runWakeUp();
      const apiOrigin = (process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
      const createUserUrl = apiOrigin
        ? `${apiOrigin}/api/admin/create-user`
        : '/api/admin/create-user';
      const createRes = await fetch(createUserUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailTrim,
          password,
          fullName: fullName?.trim() || '',
          role: roleForApi,
          allowed_tab_paths: addUserForm.tabAccessCustom ? addUserForm.tabPaths : null,
        }),
      });
      const createData = await createRes.json().catch(() => ({}));
      if (!createRes.ok) {
        const msg =
          createData?.error ||
          createData?.hint ||
          (createRes.status === 404
            ? 'Create-user API returned 404. Run `npm start` or `npm run email-api` on port 3001, or set REACT_APP_API_URL=http://127.0.0.1:3001 in .env.local'
            : `HTTP ${createRes.status}`);
        throw new Error(msg);
      }
      const roleLabel = addUserForm.tabAccessCustom ? 'Admin (custom tabs)' : (addUserForm.role || 'team member').trim();
      const emailResult = await sendHrUserInviteEmail({
        to: emailTrim,
        fullName: fullName?.trim(),
        role: roleLabel,
        email: emailTrim,
        temporaryPassword: password,
      });
      if (emailResult.success) {
        setAddUserSuccess(`User created. Invite email sent to ${emailTrim}.`);
        setAddUserError(null);
      } else {
        setAddUserSuccess(`User created for ${emailTrim}.`);
        console.warn('[TeamMembers] HR invite email failed:', emailResult.error);
        setAddUserError(
          `Invite email was not sent: ${emailResult.error || 'unknown error'}. Run \`npm run email-server\` in a separate terminal (port 3001) with GMAIL_USER and GMAIL_APP_PASSWORD in .env.`,
        );
      }
      setAddUserForm({
        email: '',
        fullName: '',
        role: 'team member',
        tabAccessCustom: false,
        tabPaths: [],
      });
      await fetchMembers();
    } catch (err) {
      setAddUserError(err?.message || 'Failed to add user.');
    } finally {
      setAddingUser(false);
    }
  };

  const openEdit = (member) => {
    if (!canEdit || !member) return;
    setSelectedMember(member);
    const tabResolved = resolveAllowedTabPaths(member.allowed_tab_paths);
    const tabAccessCustom = tabResolved !== 'legacy';
    const tabPaths = tabAccessCustom ? [...tabResolved] : [];
    setEditForm({
      full_name: member.full_name ?? '',
      role: tabAccessCustom ? 'admin' : (member.role ?? 'team member'),
      Designation: getDesignation(member),
      annual_leave_left: member.annual_leave_left != null ? String(member.annual_leave_left) : '',
      emergency_leave_left: member.emergency_leave_left != null ? String(member.emergency_leave_left) : '',
      sick_leave_left: member.sick_leave_left != null ? String(member.sick_leave_left) : '',
      avatar_url: member.avatar_url ?? '',
      active: member.active !== false,
      tabAccessCustom,
      tabPaths,
    });
    setSaveError(null);
    setShowEditModal(true);
  };

  const handleToggleActive = async (member) => {
    if (!canEdit || !member?.id) return;
    const nextActive = member.active !== false ? false : true;
    setToggleError(null);
    try {
      await runWakeUp();
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ active: nextActive, updated_at: new Date().toISOString() })
        .eq('id', member.id);
      if (updateError) throw updateError;
      await fetchMembers();
    } catch (err) {
      setToggleError(err?.message || 'Failed to update.');
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedMember?.id) return;
    const ext = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '') || 'png';
    setSaveError(null);
    setAvatarUploading(true);
    try {
      await runWakeUp();
      const path = `${selectedMember.id}/avatar.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
      setEditForm((f) => ({ ...f, avatar_url: publicUrl }));
    } catch (err) {
      setSaveError(err?.message || 'Failed to upload image.');
    } finally {
      setAvatarUploading(false);
      e.target.value = '';
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMember?.id || !canEdit) return;
    setSaveError(null);
    setSaving(true);
    try {
      await runWakeUp();
      const isSelf = selectedMember.id === profile?.id;
      const roleForSave = editForm.tabAccessCustom ? 'admin' : (editForm.role.trim() || 'team member');
      const nextRole = normalizeRole(roleForSave);
      if (isSelf && nextRole !== ROLES.ADMIN) {
        setSaveError('You cannot remove your own Admin role here.');
        return;
      }
      const payload = {
        full_name: editForm.full_name.trim() || null,
        role: roleForSave,
        Designation: editForm.Designation.trim() || null,
        avatar_url: editForm.avatar_url.trim() || null,
        active: Boolean(editForm.active),
        updated_at: new Date().toISOString(),
        allowed_tab_paths: editForm.tabAccessCustom ? editForm.tabPaths : null,
      };
      const { error } = await supabase.from('profiles').update(payload).eq('id', selectedMember.id);
      if (error) throw error;
      await fetchMembers();
      if (selectedMember.id === user?.id) {
        refreshProfile?.();
      }
      setShowEditModal(false);
      setSelectedMember(null);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const filteredMembers =
    activeTab === 'active'
      ? members.filter((m) => m.active !== false)
      : members.filter((m) => m.active === false);
  const MEMBERS = filteredMembers; // alias for list display
  const navigate = useNavigate();

  const handleExportExcel = () => {
    const header = ['Name', 'Designation', 'Role', 'Annual leave', 'Emergency leave', 'Sick leave'];
    const rows = MEMBERS.map((m) => [
      m.full_name ?? '',
      getDesignation(m) ?? '',
      m.role ?? '',
      m.annual_leave_left != null ? String(m.annual_leave_left) : '',
      m.emergency_leave_left != null ? String(m.emergency_leave_left) : '',
      m.sick_leave_left != null ? String(m.sick_leave_left) : '',
    ]);
    const csvLines = [header, ...rows].map((cols) =>
      cols
        .map((v) => {
          const s = v == null ? '' : String(v);
          return s.includes('"') || s.includes(',') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    );
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'team-members-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="team-page">
      <div className="team-card">
        <div className="team-header-row">
          <div>
            <h1 className="team-title">Team members</h1>
            {canEdit && (
              <p className="team-lede">
                Edit a member to change their role, optional custom sidebar sections, and profile details. Use{' '}
                <strong>Deactivate</strong> / <strong>Activate</strong> instead of removing someone — to reuse an email,
                open the <strong>Inactive</strong> tab and activate the existing user (you cannot add the same email twice).
              </p>
            )}
          </div>
          <div className="team-header-actions">
            {canEdit && (
              <button
                type="button"
                className="team-header-btn team-header-btn-primary"
                onClick={() => {
                  setAddUserError(null);
                  setAddUserSuccess(null);
                  setAddUserForm({
                    email: '',
                    fullName: '',
                    role: 'team member',
                    tabAccessCustom: false,
                    tabPaths: [],
                  });
                  setShowAddUserModal(true);
                }}
              >
                <IconPlus />
                Add user
              </button>
            )}
          </div>
        </div>

        <div className="team-filters-row">
          <div className="team-filters-left">
            <div className="team-layout-toggle-group" aria-label="Layout options">
              <button
                type="button"
                className={`team-layout-toggle-btn${
                  viewMode === 'table' ? ' active' : ''
                }`}
                onClick={() => setViewMode('table')}
              >
                <IconList />
              </button>
              <button
                type="button"
                className={`team-layout-toggle-btn${
                  viewMode === 'grid' ? ' active' : ''
                }`}
                onClick={() => setViewMode('grid')}
              >
                <IconGrid />
              </button>
            </div>

            <div className="team-tabs">
              <button
                type="button"
                className={`team-tab-btn${
                  activeTab === 'active' ? ' active' : ''
                }`}
                onClick={() => setActiveTab('active')}
              >
                Active members
              </button>
              <button
                type="button"
                className={`team-tab-btn${
                  activeTab === 'inactive' ? ' active' : ''
                }`}
                onClick={() => setActiveTab('inactive')}
              >
                Inactive members
              </button>
            </div>
          </div>

          <div className="team-filters-right">
            <button type="button" className="team-utility-btn" onClick={handleExportExcel} disabled={loading || MEMBERS.length === 0}>
              Excel
            </button>
            <div className="team-search">
              <input type="text" placeholder="Search" aria-label="Search team members" />
              <span className="team-search-icon">
                <IconSearch />
              </span>
            </div>
          </div>
        </div>

        {toggleError && (
          <p className="salary-form-error salary-form-error-inline" role="alert" style={{ marginBottom: '0.5rem' }}>
            {toggleError}
          </p>
        )}

        {viewMode === 'table' ? (
          <div className="team-table-wrapper">
            <table className="team-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Designation</th>
                  <th>Role</th>
                  <th>Annual leave</th>
                  <th>Emergency leave</th>
                  <th>Sick leave</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7}>Loading…</td>
                  </tr>
                ) : loadError ? (
                  <tr>
                    <td colSpan={7}>{loadError}</td>
                  </tr>
                ) : MEMBERS.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      {activeTab === 'active' ? 'No active members.' : 'No inactive members.'}
                    </td>
                  </tr>
                ) : (
                  MEMBERS.map((member) => (
                    <tr key={member.id}>
                      <td>
                        <div className="team-name-cell">
                          <div className="team-avatar">
                            {member.avatar_url ? (
                              <img src={member.avatar_url} alt="" />
                            ) : null}
                          </div>
                          <button
                            type="button"
                            className="team-name-link"
                            onClick={() => navigate(`/team/members/${member.id}`)}
                          >
                            {member.full_name || '—'}
                          </button>
                        </div>
                      </td>
                      <td>{getDesignation(member) || '—'}</td>
                      <td>{member.role ?? '—'}</td>
                      <td>{member.annual_leave_left != null ? member.annual_leave_left : '—'}</td>
                      <td>{member.emergency_leave_left != null ? member.emergency_leave_left : '—'}</td>
                      <td>{member.sick_leave_left != null ? member.sick_leave_left : '—'}</td>
                      <td>
                        <div className="team-row-actions">
                          {canEdit && (
                            <>
                              <button
                                type="button"
                                className="team-row-icon-btn"
                                aria-label="Edit member"
                                onClick={() => openEdit(member)}
                              >
                                <IconEdit />
                              </button>
                              <button
                                type="button"
                                className="team-row-text-btn"
                                onClick={() => handleToggleActive(member)}
                              >
                                {member.active === false ? 'Activate' : 'Deactivate'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="team-grid-wrapper">
            <div className="team-grid">
              {loading ? (
                <p>Loading…</p>
              ) : loadError ? (
                <p>{loadError}</p>
              ) : MEMBERS.length === 0 ? (
                <p>{activeTab === 'active' ? 'No active members.' : 'No inactive members.'}</p>
              ) : (
                MEMBERS.map((member) => (
                  <div key={member.id} className="team-grid-card">
                    <div className="team-grid-avatar">
                      {member.avatar_url ? (
                        <img src={member.avatar_url} alt="" />
                      ) : null}
                    </div>
                    <div className="team-grid-name">{member.full_name || '—'}</div>
                    <div className="team-grid-role">{member.role ?? '—'}</div>
                    <button
                      type="button"
                      className="team-grid-btn"
                      onClick={() => navigate(`/team/members/${member.id}`)}
                    >
                      View Details
                    </button>
                    {canEdit && (
                      <>
                        <button
                          type="button"
                          className="team-grid-btn"
                          onClick={() => openEdit(member)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="team-grid-btn"
                          onClick={() => handleToggleActive(member)}
                        >
                          {member.active === false ? 'Activate' : 'Deactivate'}
                        </button>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <div className="team-footer-row">
          <div className="team-footer-left">
            <select
              className="team-page-size"
              defaultValue="10"
              aria-label="Rows per page"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
            <span>{MEMBERS.length === 0 ? '0-0 / 0' : `1-${MEMBERS.length} / ${MEMBERS.length}`}</span>
          </div>
          <div className="team-footer-right">
            <button
              type="button"
              className="team-page-btn"
              aria-label="Previous page"
            >
              «
            </button>
            <button
              type="button"
              className="team-page-btn team-page-btn-active"
              aria-label="Current page"
            >
              1
            </button>
            <button
              type="button"
              className="team-page-btn"
              aria-label="Next page"
            >
              »
            </button>
          </div>
        </div>
      </div>

      {showAddUserModal && (
        <div
          className="estimates-modal-backdrop"
          onClick={() => !addingUser && setShowAddUserModal(false)}
        >
          <div className="estimates-modal team-members-modal" onClick={(e) => e.stopPropagation()}>
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Add user</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => !addingUser && setShowAddUserModal(false)}
                aria-label="Close"
                disabled={addingUser}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAddUserSubmit}>
              <div className="estimates-modal-body">
                <p className="team-invite-text">
                  Create an account. Password is auto-generated from the email (first 2 letters + 123@). If this email
                  already exists, go to the <strong>Inactive</strong> tab and <strong>Activate</strong> that user instead
                  of adding again.
                </p>
                {addUserError && (
                  <p className="salary-form-error salary-form-error-inline" role="alert">
                    {addUserError}
                  </p>
                )}
                {addUserSuccess && (
                  <p className="login-message" style={{ marginBottom: '0.75rem' }}>
                    {addUserSuccess}
                  </p>
                )}
                {addingUser && (
                  <p className="team-creating-hint" aria-live="polite">
                    Creating account, updating profile, and sending invite email…
                  </p>
                )}
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Full name</div>
                  <input
                    className="estimates-form-input"
                    placeholder="Full name"
                    value={addUserForm.fullName}
                    onChange={(e) => setAddUserForm((f) => ({ ...f, fullName: e.target.value }))}
                    disabled={addingUser}
                  />
                  <div className="estimates-form-label">Email</div>
                  <input
                    type="email"
                    className="estimates-form-input"
                    placeholder="you@example.com"
                    value={addUserForm.email}
                    onChange={(e) => setAddUserForm((f) => ({ ...f, email: e.target.value }))}
                    required
                    disabled={addingUser}
                  />
                  <div className="estimates-form-label">Generated password</div>
                  <div className="team-generated-password">
                    {addUserForm.email?.trim() ? generatePasswordFromEmail(addUserForm.email) : '—'}
                  </div>
                  <div className="team-modal-access-wrap">
                    <ProfileTabAccessFields
                      idPrefix="add-user-tab-access"
                      variant="stacked"
                      value={{
                        useCustom: addUserForm.tabAccessCustom,
                        paths: addUserForm.tabPaths,
                      }}
                      onChange={(next) =>
                        setAddUserForm((f) => ({
                          ...f,
                          tabAccessCustom: next.useCustom,
                          tabPaths: next.paths,
                          role: applyTabRoleForCustomTabs(next.useCustom, f.tabAccessCustom, f.role, false),
                        }))
                      }
                      disabled={addingUser}
                    />
                  </div>
                  <div className="estimates-form-label">Role</div>
                  <div>
                    <select
                      className="estimates-form-select"
                      value={addUserForm.role}
                      onChange={(e) => setAddUserForm((f) => ({ ...f, role: e.target.value }))}
                      disabled={addingUser || addUserForm.tabAccessCustom}
                      aria-label="Role"
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {addUserForm.tabAccessCustom && (
                      <p className="team-self-role-hint">
                        Custom tabs use <strong>Admin</strong> in the database (full RLS); only the selected sections
                        appear in the sidebar.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="estimates-modal-footer">
                <button
                  type="button"
                  className="estimates-footer-btn"
                  onClick={() => !addingUser && setShowAddUserModal(false)}
                  disabled={addingUser}
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="estimates-footer-btn estimates-footer-btn-primary team-create-user-submit"
                  disabled={addingUser}
                  aria-busy={addingUser}
                  aria-label={addingUser ? 'Creating user, please wait' : 'Create user'}
                >
                  {addingUser ? (
                    <>
                      <span className="team-create-user-spinner" aria-hidden />
                      Creating user…
                    </>
                  ) : (
                    'Create user'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selectedMember && (
        <div
          className="estimates-modal-backdrop"
          onClick={() => !saving && setShowEditModal(false)}
        >
          <div className="estimates-modal team-members-modal" onClick={(e) => e.stopPropagation()}>
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Edit member</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => !saving && setShowEditModal(false)}
                aria-label="Close"
                disabled={saving}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <div className="estimates-modal-body">
                {saveError && (
                  <p className="salary-form-error salary-form-error-inline" role="alert">
                    {saveError}
                  </p>
                )}
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Full name</div>
                  <input
                    className="estimates-form-input"
                    value={editForm.full_name}
                    onChange={(e) => setEditForm((f) => ({ ...f, full_name: e.target.value }))}
                    placeholder="Full name"
                  />

                  <div className="estimates-form-label">Designation</div>
                  <input
                    className="estimates-form-input"
                    value={editForm.Designation}
                    onChange={(e) => setEditForm((f) => ({ ...f, Designation: e.target.value }))}
                    placeholder="Designation"
                  />

                  <div className="team-modal-access-wrap">
                    <ProfileTabAccessFields
                      idPrefix={`member-edit-${selectedMember.id}`}
                      variant="stacked"
                      value={{
                        useCustom: editForm.tabAccessCustom,
                        paths: editForm.tabPaths,
                      }}
                      onChange={(next) =>
                        setEditForm((f) => ({
                          ...f,
                          tabAccessCustom: next.useCustom,
                          tabPaths: next.paths,
                          role: applyTabRoleForCustomTabs(
                            next.useCustom,
                            f.tabAccessCustom,
                            f.role,
                            selectedMember.id === profile?.id,
                          ),
                        }))
                      }
                      disabled={saving}
                    />
                  </div>

                  <div className="estimates-form-label">Role</div>
                  <div>
                    <select
                      className="estimates-form-select"
                      value={editForm.role}
                      onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                      disabled={saving || selectedMember.id === profile?.id || editForm.tabAccessCustom}
                      title={
                        selectedMember.id === profile?.id
                          ? 'You cannot change your own role here'
                          : editForm.tabAccessCustom
                            ? 'Role is Admin while custom sidebar sections are enabled'
                            : undefined
                      }
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {selectedMember.id === profile?.id && (
                      <p className="team-self-role-hint">Your role cannot be changed here.</p>
                    )}
                    {editForm.tabAccessCustom && selectedMember.id !== profile?.id && (
                      <p className="team-self-role-hint">
                        Custom tabs use <strong>Admin</strong> in the database; only the selected sections appear in the
                        sidebar.
                      </p>
                    )}
                  </div>

                  <div className="estimates-form-label">Profile photo</div>
                  <div className="team-avatar-upload-wrap">
                    {editForm.avatar_url && (
                      <img
                        src={editForm.avatar_url}
                        alt=""
                        className="team-avatar-preview"
                      />
                    )}
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      className="team-avatar-input-hidden"
                      aria-label="Upload profile photo"
                      onChange={handleAvatarUpload}
                      disabled={avatarUploading}
                    />
                    <button
                      type="button"
                      className="estimates-footer-btn"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={avatarUploading}
                    >
                      {avatarUploading ? 'Uploading…' : editForm.avatar_url ? 'Change photo' : 'Upload photo'}
                    </button>
                  </div>

                  <div className="estimates-form-label">Account active</div>
                  <label className="team-checkbox-row">
                    <input
                      type="checkbox"
                      checked={editForm.active}
                      onChange={(e) => setEditForm((f) => ({ ...f, active: e.target.checked }))}
                    />
                    <span>Active (account can be used)</span>
                  </label>

                  <div className="estimates-form-label">Annual leave left</div>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="estimates-form-input"
                    value={editForm.annual_leave_left}
                    placeholder="28"
                    readOnly
                  />

                  <div className="estimates-form-label">Emergency leave left</div>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="estimates-form-input"
                    value={editForm.emergency_leave_left}
                    placeholder="5"
                    readOnly
                  />

                  <div className="estimates-form-label">Sick leave left</div>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="estimates-form-input"
                    value={editForm.sick_leave_left}
                    placeholder="5"
                    readOnly
                  />
                </div>
              </div>

              <div className="estimates-modal-footer">
                <button
                  type="button"
                  className="estimates-footer-btn"
                  onClick={() => !saving && setShowEditModal(false)}
                  disabled={saving}
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="estimates-footer-btn estimates-footer-btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

